import _ from "underscore";
import { Source, Event, LogType } from "../../utils/logger";
import dayjs from "../../utils/dayjs";
import { htmlEscape } from "../../utils/vanilla";
import { MobileActions } from "../../store/mobile/actions";
import pageHTML from "./debugLogs.tmpl";
import DebugPageView from "./debugPageView";
import { getAllLogsAndKeysFromDb } from "../../utils/indexedDbLogger";

// Treat the user as scrolled once they move more than this many px from the top.
const SCROLL_PAUSE_THRESHOLD = 50;

export default class DebugLogsView extends DebugPageView {

    private readonly template = _.template(pageHTML);
    private sourceFilter = "ALL";
    private typeFilter = "ALL";
    private renderInterval;
    private isScrolled = false;
    private isHScrolled = false;
    private vScrollHandler: EventListener;
    private hScrollHandler: EventListener;

    constructor (options) {
        super(options);
        this.renderInterval = setInterval(() => {
            this.showLogs();
        }, 3000);
    }

    render () {
        this.el.innerHTML = this.template({ sources: Object.keys(Source).concat(Object.keys(Event)).sort(), types: Object.keys(LogType).sort() });

        this.app.store.dispatch(MobileActions.setPageState({
            title: "Logging",
            pageLevel: 2
        }));

        let logSourceSelector = <HTMLElement>document.getElementById("debug-logs-source-select");
        logSourceSelector.addEventListener("change", e => {
            let target = <HTMLInputElement>e.currentTarget;
            this.sourceFilter = target.value;
            this.isScrolled = false;
            this.showLogs();
        }, false);

        let logTypeSelector = <HTMLElement>document.getElementById("debug-logs-type-select");
        logTypeSelector.addEventListener("change", e => {
            let target = <HTMLInputElement>e.currentTarget;
            this.typeFilter = target.value;
            this.isScrolled = false;
            this.showLogs();
        }, false);

        const scrollEl = document.getElementById("scroll-content");

        const logsEl = document.getElementById("debug-logs-output__logs");

        const refreshBtn = document.getElementById("debug-logs-refresh");
        if (refreshBtn) {
            refreshBtn.addEventListener("click", () => {
                // Detach scroll listeners during refresh so layout-triggered scroll
                // events don't immediately re-set isScrolled before showLogs runs
                scrollEl?.removeEventListener("scroll", this.vScrollHandler);
                logsEl?.removeEventListener("scroll", this.hScrollHandler);
                this.isScrolled = false;
                this.isHScrolled = false;
                refreshBtn.style.display = "none";
                this.showLogs();
                setTimeout(() => {
                    scrollEl?.addEventListener("scroll", this.vScrollHandler);
                    logsEl?.addEventListener("scroll", this.hScrollHandler);
                }, 200);
            });
        }

        // Vertical scroll on the page container — clears isScrolled only when
        // also not horizontally scrolled in the logs area.
        this.vScrollHandler = (ev: globalThis.Event) => {
            const target = ev.currentTarget as HTMLElement;
            this.applyScrollState(target.scrollTop ?? 0);
        };
        scrollEl?.addEventListener("scroll", this.vScrollHandler);

        // Horizontal scroll on the logs element — pauses refresh immediately and
        // only resumes once the user has also scrolled back to the vertical top.
        this.hScrollHandler = (ev: globalThis.Event) => {
            const target = ev.currentTarget as HTMLElement;
            this.isHScrolled = (target.scrollLeft ?? 0) > 0;
            this.applyScrollState(scrollEl?.scrollTop ?? 0);
        };
        logsEl?.addEventListener("scroll", this.hScrollHandler);

        this.showLogs();
        return this;
    }

    showLogs () {
        // Pause re-renders while scrolled — tap Refresh or change a filter to resume
        if (this.isScrolled) {
            return;
        }

        this.app.setFullWidth(true);
        const logsContainer = document.getElementById("debug-logs-output__logs");
        if (!logsContainer) {
            return;
        }
        logsContainer.innerHTML = `<div class="debug-log-entry debug-log-entry--header">Time · Message · Source · Type</div>`;
        getAllLogsAndKeysFromDb().then(({logs}) => {
            const filteredLogs = logs
                .filter(log => this.sourceFilter === "ALL" || log.source === this.sourceFilter)
                .filter(log => this.typeFilter === "ALL" || log.type === this.typeFilter)
                .reverse();
            if (logsContainer) {
                filteredLogs.forEach(log => {
                    const entry = document.createElement("div");
                    entry.className = `debug-log-entry debug-log-entry--${ log.type.toLowerCase() }`;
                    entry.innerHTML = `<span class="debug-log-time">${ dayjs(log.date).format("HH:mm:ss") }</span>` +
                        ` · <span class="debug-log-message">${ htmlEscape(log.message) }</span>` +
                        ` · <span class="debug-log-source">${ htmlEscape(log.source) }</span>` +
                        ` · <span class="debug-log-type">${ htmlEscape(log.type) }</span>`;
                    logsContainer.appendChild(entry);
                });
            }
        }).catch(() => {
            if (logsContainer) {
                logsContainer.innerHTML += `<div class="debug-log-entry debug-log-entry--error">Failed to load logs from IndexedDB</div>`;
            }
        });
    }

    remove () {
        this.app.setFullWidth(false);
        window.clearInterval(this.renderInterval);
        this.renderInterval = null;
        document.getElementById("scroll-content")?.removeEventListener("scroll", this.vScrollHandler);
        document.getElementById("debug-logs-output__logs")?.removeEventListener("scroll", this.hScrollHandler);
        return this;
    }

    private applyScrollState (scrollTop: number) {
        const wasScrolled = this.isScrolled;
        this.isScrolled = scrollTop > SCROLL_PAUSE_THRESHOLD || this.isHScrolled;
        if (this.isScrolled !== wasScrolled) {
            this.updateRefreshBtn();
        }
    }

    private updateRefreshBtn () {
        const btn = document.getElementById("debug-logs-refresh");
        if (btn) {
            btn.style.display = this.isScrolled ? "block" : "none";
        }
    }
}